import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getVendorsData, saveVendorsForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { buildVendorsFormSubmitData } from '../../../formDataUtils';
import { createEmptyVendor } from '../../../FormFields/vendorFieldDefaults';
import SkeletonBlock from '../../../SkeletonBlock';
import { VENDOR_FIELDS, VENDOR_SECTIONS } from './VendorFieldRegistry';
import VendorsSectionCard from './VendorsSectionCard';

const VendorsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const vendorsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.vendorsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getVendorsData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldsByQuestionId = Object.fromEntries(
      (data?.fields ?? []).map((field) => [field.formQuestionId, field.answer])
    );

    const vendorFieldValues = Object.fromEntries(
      VENDOR_FIELDS.map((field) => {
        const vendorRows = fieldsByQuestionId[field.question.id]?.vendors ?? [];
        const allowMultiple = !!field.allowMultipleVendors;

        if (allowMultiple) {
          // FormVendorFieldWithAddMore expects an array of row wrappers: [{ vendors: [vendor] }, ...]
          const rows = vendorRows.map((vendor) => ({
            vendors: [vendor ?? createEmptyVendor({ allowMultipleVendors: true })],
          }));
          return [
            field.question.fieldName,
            rows.length > 0 ? rows : [{ vendors: [createEmptyVendor({ allowMultipleVendors: true })] }],
          ];
        }

        // FormVendorField expects a single row wrapper: { vendors: [vendor] }
        return [
          field.question.fieldName,
          { vendors: [vendorRows[0] ?? createEmptyVendor({ allowMultipleVendors: false })] },
        ];
      })
    );

    setFormData({
      transitionFacilityId: data?.transitionFacilityId,
      ...vendorFieldValues,
    });
  }, [data, setFormData]);

  const submitForm = async (allData) => {
    const payload = buildVendorsFormSubmitData(allData, VENDOR_FIELDS);

    await saveVendorsForm({
      id,
      sellerFacilityId: facilityId,
      ...payload,
    })
      .then((resp) => {
        enqueueSnackbar('Vendors data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Vendors data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      {VENDOR_SECTIONS.map((section) => (
        <VendorsSectionCard
          key={section.title}
          title={section.title}
          fields={section.fields}
          permissions={vendorsPermissions}
        />
      ))}
    </>
  );
});

export default VendorsView;

VendorsView.propTypes = {
  setFormData: PropTypes.func,
};
